import { matchPath } from 'react-router-dom';
import * as P from '@/constants/paths';

// Maps web path patterns to FullStory page names so both platforms report the
// same string for the same screen — a page is only shared if the strings match
// exactly. Names live in the org's hierarchical `Area / Child` page tree and are
// hand-authored on both sides: the mobile half is PAGE_NAMES in Viome-mfe's
// src/utils/fullstory.ts. Keep the two in sync; a screen that exists on both
// platforms MUST use the identical string, or web and mobile land on two
// different FullStory pages.
// Order matters only where two patterns can match the SAME path: matchPath runs
// with end: true, so /nutrition never swallows /nutrition/foods. The live
// hazards are parameterised patterns against their literal siblings —
// /boosters-and-blockers/:id must follow /blood-sugar, /plant-virus and
// /detail, and /questionnaires/:groupId must follow /questionnaires/success.
// Both are covered in fullstoryPageNames.test.ts.
export const PAGE_NAME_MAP: Array<{ pattern: string; name: string }> = [
  // Auth / Registration
  { pattern: P.LOGIN_PATH, name: 'Login' },
  { pattern: P.LANDING_PATH, name: 'Welcome' },
  {
    pattern: P.REGISTRATION_PERSONAL_INFORMATION,
    name: 'Registration / Personal Information',
  },
  {
    pattern: P.REGISTRATION_CONTACT_INFORMATION,
    name: 'Registration / Contact Information',
  },
  {
    pattern: P.REGISTRATION_ACCOUNT_CREATION,
    name: 'Registration / Account Creation',
  },
  {
    pattern: P.REGISTRATION_USER_CONSENT,
    name: 'Registration / Terms of Service',
  },
  { pattern: P.REGISTRATION_KIT_ID, name: 'Registration / Kit ID' },
  {
    pattern: P.REGISTRATION_ADDRESS_CONFIRMATION,
    name: 'Registration / Address Confirmation',
  },
  { pattern: P.REGISTRATION_CONFIRMATION, name: 'Registration / Success' },

  // Nutrition — specific before broad
  { pattern: P.FOOD_DETAILS_PATH, name: 'Nutrition / Foods / Detail' },
  { pattern: P.MY_FOODS_PATH, name: 'Nutrition / Foods' },
  { pattern: P.FORMULA_PATH, name: 'Nutrition / Supplements / Detail' },
  // Mobile's Nutrition tab renders this screen, so the shared name is
  // 'Nutrition' — the tab route, not a NutritionHome route.
  { pattern: P.NUTRITION_PATH, name: 'Nutrition' },

  // Health — specific before broad. The three connection levels are distinct
  // pages (list -> conditions -> detail); they previously shared one name.
  { pattern: P.BIO_AGE_PATH, name: 'Health / Biological Age' },
  { pattern: P.INFLAMMAGING_PATH, name: 'Health / Inflammaging' },
  { pattern: P.GUT_MICROBIOME_PATH, name: 'Health / Microbiome' },
  {
    pattern: P.HEALTH_CONDITION_DETAIL_PATH,
    name: 'Health / Connections / Detail',
  },
  {
    pattern: P.HEALTH_CONDITIONS_PATH,
    name: 'Health / Connections / Conditions',
  },
  { pattern: P.HEALTH_CONNECTIONS_PATH, name: 'Health / Connections' },
  { pattern: P.L2_SCORE_PATH, name: 'Health / Score' },
  { pattern: P.HEALTH_PATH, name: 'Health / Scores' },

  // Health (continued) — specific before broad
  { pattern: P.ACTIVE_MICROBES_PATH, name: 'Health / Active Microbes' },
  { pattern: P.L4_DETAIL_PATH, name: 'Health / Connections / Detail / L4' },
  {
    pattern: P.IMPROVE_MY_SCORE_DETAIL,
    name: 'Health / Connections / Detail / Improve',
  },

  // Home — specific before broad
  { pattern: P.BLOOD_SUGAR_PATH, name: 'Boosters and Blockers / Blood Sugar' },
  { pattern: P.PLANT_VIRUS_PATH, name: 'Boosters and Blockers / Plant Virus' },
  {
    pattern: P.BOOSTER_BLOCKER_DETAIL,
    name: 'Boosters and Blockers / Food Job Detail',
  },
  { pattern: P.MOOD_MEMORY_FOCUS_PATH, name: 'Boosters and Blockers / Detail' },
  { pattern: P.BOOSTERS_AND_BLOCKERS_PATH, name: 'Boosters and Blockers' },
  // Mobile has a HomeScreenResultsTimeLine entry for this, but no navigator
  // registers it, so today web is the only platform reporting this name.
  { pattern: P.RESULTS_READY_PATH, name: 'Home / Results Timeline' },
  { pattern: P.COLLECT_SAMPLE_PATH, name: 'Collect Sample' },
  { pattern: P.HOME_PATH, name: 'Home' },

  // Account
  { pattern: P.SETTINGS_PATH, name: 'Account / Settings' },
  { pattern: P.SAMPLE_DETAILS_PATH, name: 'Account / Samples / Detail' },
  { pattern: P.TRACK_SAMPLE_STATUS_PATH, name: 'Account / Samples' },
  { pattern: P.ORDER_DETAIL_PATH, name: 'Account / Orders / Detail' },
  { pattern: P.MY_REPORTS_PATH, name: 'Account / Reports' },
  { pattern: P.ACCOUNT_MANAGEMENT_PATH, name: 'Account' },

  // Shop
  { pattern: P.ADD_OR_REMOVE_PRODUCTS, name: 'Shop / Customize Formulas' },
  { pattern: P.PRODUCT_DETAIL_PAGE_PATH, name: 'Shop / PDP' },
  { pattern: P.SHOP_PATH, name: 'Shop' },

  // Questionnaires — specific before broad
  { pattern: P.QUESTIONNAIRE_SUCCESS_PATH, name: 'Questionnaires / Success' },
  {
    pattern: P.QUESTIONNAIRE_QUESTIONS_PATH,
    name: 'Questionnaires / Question',
  },
  { pattern: P.QUESTIONNAIRE_DETAILS_PATH, name: 'Questionnaires / Start' },
  { pattern: P.QUESTIONNAIRES_PATH, name: 'Questionnaires' },

  // Subscription
  { pattern: P.SMP_ORDER_DETAILS_PATH, name: 'SMP' },

  // WebViews
  { pattern: P.RECIPES_PATH, name: 'Recipes' },
  { pattern: P.WRAPPED_V2_PATH, name: 'Wrapped' },
];

export function getFullStoryPageName(pathname: string): string | null {
  for (const { pattern, name } of PAGE_NAME_MAP) {
    if (matchPath({ path: pattern, end: true }, pathname)) {
      return name;
    }
  }
  return null;
}
